import React from 'react';
import { OneLineInput } from '../⚛️atoms/OneLineInput';
import { TiptapEditor } from '../⚛️atoms/TiptapEditor';
import { BookmarkIcon } from '@heroicons/react/24/solid';
import { MarkdownWrapper } from './MarkdownWrapper';
import useLockedTranslate from '../../Hooks/useLockedTranslate';
import { getTranslation, updateTranslation } from '../../utils/translationHelper';
import { useWorkbookV2EditorGridResize } from '../../Hooks/useWorkbookV2EditorGridResize';
import { TranslateButton } from '../atoms/translateButton';
import { cloneWorkbookV2Value } from './editorUtils';

export const WorkbookV2Verse = ({ item, updateItem, editing, editingLanguage = null, workbook }) => {
  const { editGrid, gridCols } = useWorkbookV2EditorGridResize();
  const { lang } = useLockedTranslate(workbook);

  const updateEditorItem = (updates) => {
    const nextItem = cloneWorkbookV2Value(item);
    nextItem.text = updates.text ?? nextItem.text;
    nextItem.page_number = updates.page_number ?? nextItem.page_number;
    nextItem.translations = updates.translations ?? nextItem.translations;
    nextItem.options = {
      ...(nextItem.options ?? {}),
      ...(updates.options ?? {})
    };
    updateItem(nextItem);
  };

  return (
    <div className={`flex w-full items-center gap-2 ${editing && 'border-pdteal rounded-lg border-2 px-8 py-12'}`}>
      {!editing && (
        <div className="from-pdteal/10 to-pddarkblue/5 relative w-11/12 self-center rounded-2xl border-1 border-gray-200 bg-linear-150 shadow-lg backdrop-blur-2xl">
          <BookmarkIcon className="text-pdyellow absolute -top-3 right-5 size-12"></BookmarkIcon>
          <div className="px-10 py-4">
            <div className="float-right size-8"></div>
            <MarkdownWrapper>{lang === 'en' ? item.text : getTranslation(item.translations, lang, 'text', item.text)}</MarkdownWrapper>
            <div className="text-pdteal pt-4 text-2xl font-semibold uppercase">
              {lang === 'en'
                ? item.options?.attribution
                : getTranslation(item.options?.translations, lang, 'attribution', item.options?.attribution)}
            </div>
          </div>
        </div>
      )}
      {editing && (
        <div className={`grid w-full gap-8 ${gridCols} transition-all duration-300 ease-out`} ref={editGrid}>
          <div className={`relative ${!editingLanguage && 'col-span-2'}`}>
            <p className="mb-2 text-xl/6 font-medium text-gray-900">Verse</p>
            <TiptapEditor content={item.text} onChange={(value) => updateEditorItem({ text: value })} />
            {editingLanguage && (
              <TranslateButton
                text={item.text}
                language={editingLanguage}
                onResponse={(value) =>
                  updateEditorItem({
                    translations: updateTranslation(item.translations ?? {}, editingLanguage, 'text', value)
                  })
                }
              />
            )}
          </div>
          {editingLanguage && (
            <div>
              <p className="mb-2 text-xl/6 font-medium text-gray-900">Verse ({editingLanguage})</p>
              <TiptapEditor
                content={getTranslation(item.translations, editingLanguage, 'text')}
                onChange={(value) =>
                  updateEditorItem({
                    translations: updateTranslation(item.translations ?? {}, editingLanguage, 'text', value)
                  })
                }
              />
            </div>
          )}
          <div className="col-start-1 w-2/5">
            <OneLineInput
              value={item.page_number}
              label="Page Number"
              onChange={(value) => updateEditorItem({ page_number: value })}
            />
          </div>
          <div className={`w-3/4 ${editingLanguage && 'col-start-1'}`}>
            <div className="relative">
              <OneLineInput
                value={item.options?.attribution}
                onChange={(value) => updateEditorItem({ options: { attribution: value } })}
                label="Attribution"
              />
              {editingLanguage && (
                <TranslateButton
                  text={item.options?.attribution}
                  language={editingLanguage}
                  onResponse={(value) =>
                    updateEditorItem({
                      options: {
                        translations: updateTranslation(
                          item.options?.translations ?? {},
                          editingLanguage,
                          'attribution',
                          value
                        )
                      }
                    })
                  }
                />
              )}
            </div>
          </div>
          {editingLanguage && (
            <OneLineInput
              value={getTranslation(item.options?.translations, editingLanguage, 'attribution')}
              onChange={(value) =>
                updateEditorItem({
                  options: {
                    translations: updateTranslation(
                      item.options?.translations ?? {},
                      editingLanguage,
                      'attribution',
                      value
                    )
                  }
                })
              }
              label={`Attribution (${editingLanguage})`}
            />
          )}
        </div>
      )}
    </div>
  );
};
